/**
 * Validation utilities for scenario conditions
 */

interface ConditionFormData {
	startDate?: string;
	endDate?: string;
	arrivingSince?: string;
	arrivingUntil?: string;
	leavingSince?: string;
	leavingUntil?: string;
	weeklyDateSetId?: number;
	appliesOnHolidays: boolean;
	minStayMinutes?: number;
	maxStayMinutes?: number;
	appliesToSpecificVehicleTypes: boolean;
	appliesToSpecificCustomerCategories: boolean;
	appliesToSpecificPaymentMethods: boolean;
	isOverstay: boolean;
	isContractOverstay: boolean;
	selectedVehicleTypes: string[];
	selectedCustomerCategories: string[];
	selectedPaymentMethods: string[];
	selectedDaysOfWeek: string[];
	operator: 'include' | 'exclude';
}

/**
 * Validates if a condition has meaningful actions/criteria configured.
 * A condition is considered to have actions if it has at least one of:
 * - Date range (startDate or endDate)
 * - Time restrictions (arrival or departure times)
 * - Stay duration limits (min or max stay)
 * - Weekly date set
 * - Days of week selection
 * - Holiday application
 * - Overstay conditions
 * - Vehicle type restrictions
 * - Customer category restrictions
 */
export const validateConditionHasActions = (
	condition: ConditionFormData
): boolean => {
	// Check date range
	if (condition.startDate || condition.endDate) {
		return true;
	}

	// Check time restrictions
	if (
		condition.arrivingSince ||
		condition.arrivingUntil ||
		condition.leavingSince ||
		condition.leavingUntil
	) {
		return true;
	}

	// Check stay duration
	if (condition.minStayMinutes || condition.maxStayMinutes) {
		return true;
	}

	// Check weekly date set
	if (condition.weeklyDateSetId) {
		return true;
	}

	// Check days of week selection
	if (
		condition.selectedDaysOfWeek &&
		condition.selectedDaysOfWeek.length > 0
	) {
		return true;
	}

	// Check holiday application
	if (condition.appliesOnHolidays) {
		return true;
	}

	// Check overstay conditions
	if (condition.isOverstay || condition.isContractOverstay) {
		return true;
	}

	// Check vehicle type restrictions
	if (
		condition.appliesToSpecificVehicleTypes &&
		condition.selectedVehicleTypes.length > 0
	) {
		return true;
	}

	// Check customer category restrictions
	if (
		condition.appliesToSpecificCustomerCategories &&
		condition.selectedCustomerCategories.length > 0
	) {
		return true;
	}

	// Check payment method restrictions
	if (
		condition.appliesToSpecificPaymentMethods &&
		condition.selectedPaymentMethods.length > 0
	) {
		return true;
	}

	return false;
};

/**
 * Gets a descriptive message about what actions are missing from a condition
 */
export const getConditionActionsSummary = (
	condition: ConditionFormData
): string => {
	const actions: string[] = [];

	if (condition.startDate || condition.endDate) {
		actions.push('Date range');
	}

	if (
		condition.arrivingSince ||
		condition.arrivingUntil ||
		condition.leavingSince ||
		condition.leavingUntil
	) {
		actions.push('Time restrictions');
	}

	if (condition.minStayMinutes || condition.maxStayMinutes) {
		actions.push('Stay duration');
	}

	if (condition.weeklyDateSetId) {
		actions.push('Weekly schedule');
	}

	if (
		condition.selectedDaysOfWeek &&
		condition.selectedDaysOfWeek.length > 0
	) {
		actions.push(`${condition.selectedDaysOfWeek.length} day(s) of week`);
	}

	if (condition.appliesOnHolidays) {
		actions.push('Holiday application');
	}

	if (condition.isOverstay || condition.isContractOverstay) {
		actions.push('Overstay conditions');
	}

	if (
		condition.appliesToSpecificVehicleTypes &&
		condition.selectedVehicleTypes.length > 0
	) {
		actions.push(
			`${condition.selectedVehicleTypes.length} vehicle type(s)`
		);
	}

	if (
		condition.appliesToSpecificCustomerCategories &&
		condition.selectedCustomerCategories.length > 0
	) {
		actions.push(
			`${condition.selectedCustomerCategories.length} customer category(s)`
		);
	}

	if (
		condition.appliesToSpecificPaymentMethods &&
		condition.selectedPaymentMethods.length > 0
	) {
		actions.push(
			`${condition.selectedPaymentMethods.length} payment method(s)`
		);
	}

	return actions.length > 0 ? actions.join(', ') : 'No actions configured';
};

/**
 * Validates all conditions in a scenario have meaningful actions
 */
export const validateAllConditionsHaveActions = (
	conditions: ConditionFormData[]
): { isValid: boolean; invalidConditions: number[] } => {
	const invalidConditions: number[] = [];

	conditions.forEach((condition, index) => {
		if (!validateConditionHasActions(condition)) {
			invalidConditions.push(index + 1); // 1-based indexing for user display
		}
	});

	return {
		isValid: invalidConditions.length === 0,
		invalidConditions
	};
};
